import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface FormDataState {
  // Store form data by form key (e.g., 'add-employee', 'edit-employee', etc.)
  forms: Record<string, {
    formData: Record<string, unknown>;
    currentStepIndex?: number;
    lastUpdated?: number;
  }>;

  // Actions
  setFormData: (formKey: string, data: Record<string, unknown>) => void;
  updateFormData: (formKey: string, data: Record<string, unknown>) => void;
  getFormData: (formKey: string) => Record<string, unknown> | null;
  setCurrentStepIndex: (formKey: string, index: number) => void;
  getCurrentStepIndex: (formKey: string) => number | undefined;
  clearFormData: (formKey: string) => void;
  clearAllForms: () => void;
}

/**
 * Generic Zustand store for form data persistence
 * Automatically persists to localStorage using Zustand's persist middleware
 * 
 * Usage:
 *   const { setFormData, getFormData, clearFormData } = useFormStore();
 *   setFormData('add-employee', { name: 'John', email: 'john@example.com' });
 *   const data = getFormData('add-employee');
 *   clearFormData('add-employee');
 */
export const useFormStore = create<FormDataState>()(
  persist(
    (set, get) => ({
      // Initial state
      forms: {},

      // Actions
      setFormData: (formKey, data) =>
        set((state) => ({
          forms: {
            ...state.forms,
            [formKey]: {
              formData: data,
              currentStepIndex: state.forms[formKey]?.currentStepIndex ?? 0,
              lastUpdated: Date.now(),
            },
          },
        })),

      updateFormData: (formKey, data) =>
        set((state) => ({
          forms: {
            ...state.forms,
            [formKey]: {
              formData: {
                ...(state.forms[formKey]?.formData || {}),
                ...data,
              },
              currentStepIndex: state.forms[formKey]?.currentStepIndex ?? 0,
              lastUpdated: Date.now(),
            },
          },
        })),

      getFormData: (formKey) => {
        const state = get();
        return state.forms[formKey]?.formData || null;
      },

      setCurrentStepIndex: (formKey, index) =>
        set((state) => ({
          forms: {
            ...state.forms,
            [formKey]: {
              formData: state.forms[formKey]?.formData || {},
              currentStepIndex: index,
              lastUpdated: state.forms[formKey]?.lastUpdated ?? Date.now(),
            },
          },
        })),

      getCurrentStepIndex: (formKey) => {
        const state = get();
        return state.forms[formKey]?.currentStepIndex;
      },

      clearFormData: (formKey) =>
        set((state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [formKey]: _removed, ...rest } = state.forms;
          return { forms: rest };
        }),

      clearAllForms: () =>
        set({ forms: {} }),
    }),
    {
      name: 'form-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
    }
  )
);

